import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Sparkles, Trash2, Edit, Search, CheckSquare } from "lucide-react";

function PuzzleLibrary({ selected, setSelected }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [difficulty, setDifficulty] = useState("");
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [busyGen, setBusyGen] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  const limit = 24;

  const load = useCallback(async () => {
    const r = await api.get(`/admin/puzzles?skip=${page*limit}&limit=${limit}&search=${encodeURIComponent(search)}&difficulty=${difficulty}`);
    setItems(r.data.items); setTotal(r.data.total);
  }, [page, search, difficulty]);

  useEffect(() => { load(); }, [load]);

  const toggle = (id) => {
    setSelected((s) => s.includes(id) ? s.filter((x)=>x!==id) : [...s, id]);
  };

  const remove = async (id) => {
    if (!confirm("Delete this puzzle from library?")) return;
    await api.delete(`/admin/puzzles/${id}`);
    toast.success("Deleted");
    load();
  };

  const genImage = async (id) => {
    setBusyGen(id);
    try {
      await api.post(`/admin/puzzles/${id}/generate-image`);
      toast.success("Image generated via Gemini Nano Banana");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally { setBusyGen(null); }
  };

  const bulkGen = async () => {
    const count = prompt("How many puzzles to generate images for? (only missing-image puzzles)", "20");
    if (!count) return;
    setBulkBusy(true);
    try {
      const r = await api.post(`/admin/puzzles/bulk-generate-images?limit=${count}&only_missing=true`);
      toast.success(`Bulk gen: ${r.data.success} ok, ${r.data.failed} failed (of ${r.data.requested})`);
      load();
    } catch (e) {
      toast.error("Bulk generation failed");
    } finally { setBulkBusy(false); }
  };

  const clearAll = async () => {
    if (!confirm("Delete ALL puzzles and their images from the library? This also removes their schedules and cannot be undone.")) return;
    if (!confirm("Are you absolutely sure? This wipes the entire Rebus library.")) return;
    try {
      const r = await api.delete(`/admin/puzzles`);
      toast.success(`Cleared ${r.data.deleted_puzzles} puzzles and ${r.data.deleted_schedules} schedules`);
      setSelected([]);
      setPage(0);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Clear failed");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 items-center">
        <div className="flex items-center gap-1 border-2 border-black bg-white px-2 brutal-shadow-sm">
          <Search size={16}/>
          <input
            data-testid="puzzle-search"
            value={search}
            onChange={(e)=> { setPage(0); setSearch(e.target.value); }}
            placeholder="Search…"
            className="px-2 py-2 outline-none font-bold"
          />
        </div>
        <select
          data-testid="puzzle-difficulty-filter"
          value={difficulty}
          onChange={(e)=>{ setPage(0); setDifficulty(e.target.value); }}
          className="border-2 border-black bg-white px-3 py-2 font-bold brutal-shadow-sm"
        >
          <option value="">All difficulties</option>
          <option value="easy">Easy</option>
          <option value="medium">Medium</option>
          <option value="hard">Hard</option>
        </select>
        <button data-testid="create-puzzle-btn" onClick={()=>setCreating(true)} className="brutal-btn-secondary flex items-center gap-2">
          <Plus size={16}/> New Puzzle
        </button>
        <button data-testid="bulk-gen-btn" disabled={bulkBusy} onClick={bulkGen} className="brutal-btn-secondary flex items-center gap-2">
          <Sparkles size={16}/> {bulkBusy ? "Generating…" : "Bulk AI Images"}
        </button>
        <button data-testid="clear-all-puzzles-btn" onClick={clearAll} className="border-2 border-black bg-[#FF3B30] text-white px-3 py-2 font-black uppercase brutal-shadow-sm flex items-center gap-2 hover:translate-y-[-1px] transition-transform">
          <Trash2 size={16}/> Clear All
        </button>
        <div className="ml-auto text-sm font-bold">
          {selected.length > 0 && (
            <span className="bg-[#FFD600] border-2 border-black px-3 py-1">{selected.length} selected</span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((p) => (
          <div
            key={p.id}
            className={`brutal-card p-4 cursor-pointer relative ${selected.includes(p.id) ? "bg-[#FFFCE5] outline outline-4 outline-[#FFD600]" : ""}`}
            onClick={() => toggle(p.id)}
            data-testid={`puzzle-${p.id}`}
          >
            <div className="absolute top-2 right-2 flex gap-1" onClick={(e)=>e.stopPropagation()}>
              <button title="Generate image" onClick={()=>genImage(p.id)} disabled={busyGen===p.id} className="p-1 border-2 border-black bg-white hover:bg-[#39FF14]">
                <Sparkles size={14}/>
              </button>
              <button title="Edit" onClick={()=>setEditing(p)} className="p-1 border-2 border-black bg-white hover:bg-blue-200">
                <Edit size={14}/>
              </button>
              <button title="Delete" onClick={()=>remove(p.id)} className="p-1 border-2 border-black bg-white hover:bg-[#FF3B30] hover:text-white">
                <Trash2 size={14}/>
              </button>
            </div>
            {p.image_data ? (
              <img src={p.image_data} className="w-full h-32 object-contain" alt=""/>
            ) : (
              <div className="border-2 border-[#FF3B30] p-3 text-center text-lg font-black whitespace-pre-line h-32 overflow-hidden">
                {p.visual}
              </div>
            )}
            <div className="mt-2">
              <div className="font-black uppercase text-sm">{p.answer}</div>
              <div className="text-xs text-neutral-500 flex gap-2 mt-1">
                <span>{p.category}</span><span>·</span><span>{p.difficulty}</span>
                {p.ai_generated && <span className="text-[#FF3B30]">· AI</span>}
              </div>
            </div>
            {selected.includes(p.id) && (
              <div className="absolute top-2 left-2 bg-[#FFD600] border-2 border-black p-1"><CheckSquare size={14}/></div>
            )}
          </div>
        ))}
      </div>

      <div className="flex justify-between items-center pt-2">
        <button disabled={page===0} onClick={()=>setPage(p=>p-1)} className="brutal-btn-secondary disabled:opacity-30">Prev</button>
        <span className="font-mono text-sm">Page {page+1} / {Math.max(1, Math.ceil(total/limit))} · {total} total</span>
        <button disabled={(page+1)*limit >= total} onClick={()=>setPage(p=>p+1)} className="brutal-btn-secondary disabled:opacity-30">Next</button>
      </div>

      {(editing || creating) && (
        <PuzzleEditor
          puzzle={editing}
          onClose={()=> { setEditing(null); setCreating(false); }}
          onSaved={()=>{ setEditing(null); setCreating(false); load(); }}
        />
      )}
    </div>
  );
}

function PuzzleEditor({ puzzle, onClose, onSaved }) {
  const [form, setForm] = useState(puzzle || { visual: "", answer: "", hint: "", difficulty: "medium", category: "General" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      if (puzzle) await api.put(`/admin/puzzles/${puzzle.id}`, form);
      else await api.post(`/admin/puzzles`, form);
      toast.success("Saved"); onSaved();
    } catch (e) { toast.error("Save failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o)=>!o && onClose()}>
      <DialogContent className="border-4 border-black rounded-none brutal-shadow max-w-lg">
        <DialogHeader><DialogTitle className="uppercase font-black">{puzzle ? "Edit" : "New"} Puzzle</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-black uppercase">Visual</label>
            <textarea data-testid="puzzle-visual-input" value={form.visual} onChange={(e)=>setForm({...form, visual:e.target.value})} className="brutal-input" rows={3}/>
          </div>
          <div>
            <label className="text-xs font-black uppercase">Answer</label>
            <input data-testid="puzzle-answer-input" value={form.answer} onChange={(e)=>setForm({...form, answer:e.target.value})} className="brutal-input"/>
          </div>
          <div>
            <label className="text-xs font-black uppercase">Hint</label>
            <input value={form.hint || ""} onChange={(e)=>setForm({...form, hint:e.target.value})} className="brutal-input"/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-black uppercase">Difficulty</label>
              <select value={form.difficulty} onChange={(e)=>setForm({...form, difficulty:e.target.value})} className="brutal-input">
                <option value="easy">Easy</option><option value="medium">Medium</option><option value="hard">Hard</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-black uppercase">Category</label>
              <input value={form.category} onChange={(e)=>setForm({...form, category:e.target.value})} className="brutal-input"/>
            </div>
          </div>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="brutal-btn-secondary">Cancel</button>
          <button data-testid="save-puzzle-btn" onClick={save} disabled={busy} className="brutal-btn">{busy?"Saving…":"Save"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default PuzzleLibrary;
